// Keep an open modal within the visual viewport so the mobile on-screen
// keyboard can't cover the dialog. The `.modal__overlay` is `position:
// fixed; inset: 0`, which pins it to the *layout* viewport — when the soft
// keyboard opens it shrinks the *visual* viewport instead, leaving the
// centered dialog half-hidden behind the keyboard.
//
// While a keyboard inset is present we pin the overlay to the visible
// region (via the VisualViewport API) and top-align the dialog so the whole
// thing sits above the keyboard. With no keyboard we leave the default
// centered, full-screen layout untouched, so desktop is unaffected.

// Below this many CSS px we treat the difference as browser chrome (the URL
// bar hiding/showing), not a soft keyboard.
const KEYBOARD_INSET_THRESHOLD = 150;

/**
 * Start tracking the visual viewport for `overlay`. Self-cleans (removes its
 * listeners) once the overlay is detached from the DOM, so callers don't need
 * to manage teardown — closing the modal removes the overlay as usual.
 */
export function keepOverlayAboveKeyboard(overlay: HTMLElement): void {
  const vv = window.visualViewport;
  if (!vv) return; // Older browsers: keep the default centered layout.

  const update = () => {
    if (!overlay.isConnected) {
      teardown();
      return;
    }
    const inset = window.innerHeight - vv.height;
    if (inset > KEYBOARD_INSET_THRESHOLD) {
      // Shrink the overlay to the visible area above the keyboard and
      // top-align the dialog within it. Only the vertical axis changes —
      // the keyboard doesn't move things horizontally, and leaving the
      // `inset: 0` left/right edges intact keeps horizontal centering
      // correct (and avoids any box-sizing surprises from an explicit
      // width).
      overlay.style.top = `${vv.offsetTop}px`;
      overlay.style.bottom = "auto";
      overlay.style.height = `${vv.height}px`;
      overlay.style.alignItems = "flex-start";
    } else {
      // No keyboard: restore the centered, full-screen overlay.
      overlay.style.top = "";
      overlay.style.bottom = "";
      overlay.style.height = "";
      overlay.style.alignItems = "";
    }
  };

  const teardown = () => {
    vv.removeEventListener("resize", update);
    vv.removeEventListener("scroll", update);
    observer.disconnect();
  };

  const observer = new MutationObserver(() => {
    if (!overlay.isConnected) teardown();
  });
  observer.observe(document.body, { childList: true });

  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update);
  update();
}
